import { SchoolAnchor } from "@/qualification/model/SchoolAnchor"
import { VendorAnchor } from "@/qualification/model/VendorAnchor"
import { startsWith } from "@/util/StringUtils"

const catchImport = (error: any) => {
  const isChunkLoadFailed = startsWith(error.message, "Loading chunk") || startsWith(error.message, "Loading CSS chunk") || startsWith(error.message, "Failed to fetch dynamically")
  if (isChunkLoadFailed) {
    location.reload()
  }
}

const Routes = {
  Auth: {
    Login: {
      path: "/login",
      name: "Login",
      component: () => import("@/auth/view/login/Login.vue").catch(catchImport),
      single: true,
    },
    Join: {
      path: "/join",
      name: "Join",
      component: () => import("@/auth/view/Join.vue").catch(catchImport),
      single: true,
    },
    ForgetPassword: {
      path: "/forget-password",
      name: "ForgetPassword",
      component: () => import("@/auth/view/ForgetPassword.vue").catch(catchImport),
      single: true,
    },
  },
  MiniProgram: {
    MiniProgramAuthGuard: {
      path: "/mp-auth",
      name: "MiniProgramAuthGuard",
      component: () => import("@/page/mp/MiniProgramAuthGuard.vue").catch(catchImport),
      single: true,
    },
    Signature: {
      path: "/mp/signature",
      name: "MpSignature",
      component: () => import("@/page/mp/signature/SignaturePage.vue").catch(catchImport),
      single: true,
      title: "签名",
    },
    PrinterGuide: {
      path: "/mp/printer-guide",
      name: "MpPrinterGuide",
      component: () => import("@/page/mp/common/printerGuide/PrinterGuide.vue").catch(catchImport),
      single: true,
      title: "打印机适配说明",
    },
    SignatureTask: {
      path: "/mp/signature/task",
      name: "SignatureTask",
      component: () => import("@/page/mp/signature/task/SignatureTask.vue").catch(catchImport),
      single: true,
      title: "签名任务",
      build(taskId: string, autoBack: boolean = false) {
        return `/mp/signature/task?taskId=${taskId}${autoBack ? "&autoBack=1" : ""}`
      },
    },
    Eb: {
      Meal: {
        SampleList: {
          path: "/mp/eb/:supervisorId/meal/sample",
          name: "MpEbMealSampleList",
          component: () => import("@/page/mp/eb/meal/sample/SampleListPage.vue").catch(catchImport),
          single: true,
          props: true,
          title: "留样管理",
          build(supervisorId: string) {
            return `/mp/eb/${supervisorId}/meal/sample`
          },
        },
      },
      Goods: {
        List: {
          path: "/mp/eb/:supervisorId/goods",
          name: "MpEbGoodsList",
          component: () => import("@/page/mp/eb/goods/GoodsList.vue").catch(catchImport),
          single: true,
          props: true,
          title: "商品管理",
          build(supervisorId: string) {
            return `/mp/eb/${supervisorId}/goods`
          },
        },
      },
      Dashboard: {
        path: "/mp/eb/:supervisorId/dashboard",
        name: "MpEbDashboard",
        component: () => import("@/page/mp/eb/dashboard/DashBoard.vue").catch(catchImport),
        single: true,
        props: true,
        title: "数据驾驶舱",
      },
      Order: {
        List: {
          path: "/mp/eb/:supervisorId/order/list",
          name: "MpEbOrderList",
          component: () => import("@/page/mp/eb/order/list/OrderList.vue").catch(catchImport),
          single: true,
          props: true,
          build(supervisorId: string) {
            return `/mp/eb/${supervisorId}/order/list`
          },
        },
        Detail: {
          path: "/mp/eb/:supervisorId/order/:orderId/detail",
          name: "MpEbOrderDetail",
          component: () => import("@/page/mp/eb/order/detail/OrderDetail.vue").catch(catchImport),
          single: true,
          props: true,
          build(supervisorId: string, orderId: string) {
            return `/mp/eb/${supervisorId}/order/${orderId}/detail`
          },
        },
      },
      Rectification: {
        List: {
          path: "/mp/eb/:supervisorId/rectification/list",
          name: "MpEbRectificationList",
          component: () => import("@/page/mp/eb/rectification/list/RectificationList.vue").catch(catchImport),
          single: true,
          props: true,
          title: "整改管理",
          build(supervisorId: string) {
            return `/mp/eb/${supervisorId}/rectification/list`
          },
        },
        BatchDetail: {
          path: "/mp/eb/:supervisorId/rectification/:batchId/detail",
          name: "MpEbRectificationBatchDetail",
          component: () => import("@/page/mp/eb/rectification/detail/BatchDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "整改批次详情",
          build(supervisorId: string, batchId: string) {
            return `/mp/eb/${supervisorId}/rectification/${batchId}/detail`
          },
        },
        TicketDetail: {
          path: "/mp/eb/:supervisorId/rectification/ticket/:ticketId/detail",
          name: "MpEbRectificationTicketDetail",
          component: () => import("@/page/mp/eb/rectification/detail/TicketDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "整改单详情",
          meta: { isKeepAlive: true },
          build(supervisorId: string, ticketId: string) {
            return `/mp/eb/${supervisorId}/rectification/ticket/${ticketId}/detail`
          },
        },
      },
      Bid: {
        List: {
          path: "/mp/eb/:organizationId/bid/project",
          name: "MpEbBidProjectList",
          component: () => import("@/page/mp/eb/bid/ProjectListPage.vue").catch(catchImport),
          single: true,
          props: true,
          title: "竞标项目",
          build(organizationId: string) {
            return `/mp/eb/${organizationId}/bid/project`
          },
        },
        Credit: {
          Management: {
            path: "/mp/regulator/:regulatorId/credit/management",
            name: "MpRegulatorCreditManagement",
            component: () => import("@/page/mp/eb/credit/CreditManagement.vue").catch(catchImport),
            single: true,
            props: true,
            title: "供应商管理",
            build(regulatorId: string) {
              return `/mp/regulator/${regulatorId}/credit/management`
            },
          },
        },
      },
      Ticket: {
        List: {
          path: "/mp/eb/:supervisorId/ticket/list",
          name: "MpEbTicketList",
          component: () => import("@/page/mp/eb/ticket/list/TicketList.vue").catch(catchImport),
          single: true,
          props: true,
          title: "监管意见",
          build(supervisorId: string) {
            return `/mp/eb/${supervisorId}/ticket/list`
          },
        },
        Detail: {
          path: "/mp/eb/:supervisorId/ticket/:ticketId/detail",
          name: "MpEbTicketDetail",
          component: () => import("@/page/mp/eb/ticket/detail/TicketDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "监管意见详情",
          build(supervisorId: string, ticketId: string) {
            return `/mp/eb/${supervisorId}/ticket/${ticketId}/detail`
          },
        },
      },
    },
    School: {
      Dashboard: {
        path: "/mp/school/:schoolId/dashboard",
        name: "MpSchoolDashboard",
        component: () => import("@/page/mp/school/dashboard/DashBoard.vue").catch(catchImport),
        single: true,
        props: true,
        title: "数据驾驶舱",
      },
      Rectification: {
        List: {
          path: "/mp/school/:schoolId/rectification/list",
          name: "MpSchoolRectificationList",
          component: () => import("@/page/mp/school/rectification/list/RectificationList.vue").catch(catchImport),
          single: true,
          props: true,
          title: "整改管理",
          build(schoolId: string) {
            return `/mp/school/${schoolId}/rectification/list`
          },
        },
        TicketDetail: {
          path: "/mp/school/:schoolId/rectification/:ticketId/detail",
          name: "MpSchoolRectificationTicketDetail",
          component: () => import("@/page/mp/school/rectification/detail/TicketDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "整改单详情",
          meta: { isKeepAlive: true },
          build(schoolId: string, ticketId: string) {
            return `/mp/school/${schoolId}/rectification/${ticketId}/detail`
          },
        },
      },
      Reservation: {
        Home: {
          path: "/mp/school/:schoolId/reservation/home",
          name: "MpSchoolReservationHome",
          component: () => import("@/page/mp/school/reservation/Reservation.vue").catch(catchImport),
          single: true,
          props: true,
          title: "预定",
          build(schoolId: string) {
            return `/mp/school/${schoolId}/reservation`
          },
        },
        List: {
          path: "/mp/school/:schoolId/reservation",
          name: "MpSchoolReservation",
          component: () => import("@/page/mp/school/reservation/VendorList.vue").catch(catchImport),
          single: true,
          props: true,
          title: "选择供应商",
          build(schoolId: string) {
            return `/mp/school/${schoolId}/reservation/vendorList`
          },
        },
        VendorHome: {
          path: "/mp/school/:schoolId/reservation/vendorHome",
          name: "MpSchoolReservationVendorHome",
          component: () => import("@/page/mp/school/reservation/VendorHome.vue").catch(catchImport),
          single: true,
          props: true,
          title: "预定",
          meta: {
            isKeepAlive: true,
          },
          build(schoolId: string) {
            return `/mp/school/${schoolId}/reservation/vendorHome`
          },
        },
      },
      EmergencyOrder: {
        path: "/mp/school/:schoolId/emergencyOrder",
        name: "MpSchoolEmergencyOrder",
        component: () => import("@/page/mp/school/emergencyOrder/EmergencyOrderHome.vue").catch(catchImport),
        single: true,
        props: true,
        title: "紧急下单",
        build(schoolId: string) {
          return `/mp/school/${schoolId}/emergencyOrder`
        },
      },
      NutritionOrder: {
        List: {
          path: "/mp/school/:schoolId/nutritionOrder",
          name: "MpSchoolNutritionOrder",
          component: () => import("@/page/mp/school/nutritionOrder/VendorList.vue").catch(catchImport),
          single: true,
          props: true,
          title: "营养计划下单",
          build(schoolId: string) {
            return `/mp/school/${schoolId}/nutritionOrder`
          },
        },
        VendorHome: {
          path: "/mp/school/:schoolId/nutritionOrder/vendorHome",
          name: "MpSchoolNutritionOrderVendorHome",
          component: () => import("@/page/mp/school/nutritionOrder/VendorHome.vue").catch(catchImport),
          single: true,
          props: true,
          title: "营养计划下单",
          meta: {
            isKeepAlive: true,
          },
          build(schoolId: string) {
            return `/mp/school/${schoolId}/nutritionOrder/vendorHome`
          },
        },
      },
      SelfPaidOrder: {
        List: {
          path: "/mp/school/:schoolId/selfPaidOrder",
          name: "MpSchoolSelfPaidOrder",
          component: () => import("@/page/mp/school/selfPaidOrder/VendorList.vue").catch(catchImport),
          single: true,
          props: true,
          title: "5+N自费下单",
          build(schoolId: string) {
            return `/mp/school/${schoolId}/selfPaidOrder`
          },
        },
        VendorHome: {
          path: "/mp/school/:schoolId/selfPaidOrder/vendorHome",
          name: "MpSchoolSelfPaidOrderVendorHome",
          component: () => import("@/page/mp/school/selfPaidOrder/VendorHome.vue").catch(catchImport),
          single: true,
          props: true,
          title: "5+N自费下单",
          meta: {
            isKeepAlive: true,
          },
          build(schoolId: string) {
            return `/mp/school/${schoolId}/selfPaidOrder/vendorHome`
          },
        },
      },
      Order: {
        Management: {
          path: "/mp/school/:schoolId/order",
          name: "MpSchoolOrderManagement",
          component: () => import("@/page/mp/school/order/management/OrderManagement.vue").catch(catchImport),
          single: true,
          props: true,
          title: "我的订单",
          meta: {
            isKeepAlive: true,
          },
          build(schoolId: string) {
            return `/mp/school/${schoolId}/order`
          },
        },
        Detail: {
          path: "/mp/school/:schoolId/order/:orderId/detail",
          name: "MpSchoolOrderDetail",
          component: () => import("@/page/mp/school/order/detail/OrderDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "订单详情",
          meta: { isKeepAlive: true },
          build(schoolId: string, orderId: string) {
            return `/mp/school/${schoolId}/order/${orderId}/detail`
          },
        },
        Check: {
          path: "/mp/school/:schoolId/order/:orderId/check",
          name: "MpSchoolOrderCheck",
          component: () => import("@/page/mp/school/order/check/OrderCheck.vue").catch(catchImport),
          single: true,
          props: true,
          title: "订单详情",
          meta: { isKeepAlive: true },
          build(schoolId: string, orderId: string) {
            return `/mp/school/${schoolId}/order/${orderId}/check`
          },
        },
        DoubleCheck: {
          path: "/mp/school/:schoolId/order/:orderId/doubleCheck",
          name: "MpSchoolOrderDoubleCheck",
          component: () => import("@/page/mp/school/order/doubleCheck/OrderDoubleCheck.vue").catch(catchImport),
          single: true,
          props: true,
          title: "订单详情",
          meta: { isKeepAlive: true },
          build(schoolId: string, orderId: string) {
            return `/mp/school/${schoolId}/order/${orderId}/doubleCheck`
          },
        },
        Refund: {
          path: "/mp/school/:schoolId/order/:orderId/refund",
          name: "MpSchoolOrderRefund",
          component: () => import("@/page/mp/school/order/refund/OrderRefund.vue").catch(catchImport),
          single: true,
          props: true,
          title: "修改订单数量",
          build(schoolId: string, orderId: string) {
            return `/mp/school/${schoolId}/order/${orderId}/refund`
          },
        },
        Remark: {
          path: "/mp/school/:schoolId/order/remark",
          name: "MpSchoolOrderRemark",
          component: () => import("@/page/mp/school/reservation/remark/BuyerRemark.vue").catch(catchImport),
          single: true,
          props: true,
          title: "订单备注",
          build(schoolId: string) {
            return `/mp/school/${schoolId}/order/remark`
          },
        },
        ProductRemark: {
          path: "/mp/school/:schoolId/order/productRemark/:skuId",
          name: "MpSchoolProductRemark",
          component: () => import("@/page/mp/school/reservation/remark/ProductRemark.vue").catch(catchImport),
          single: true,
          props: true,
          title: "编辑商品备注",
          build(schoolId: string, skuId: string) {
            return `/mp/school/${schoolId}/order/productRemark/${skuId}`
          },
        },
        ReportDetail: {
          path: "/mp/school/order/report/:reportId",
          name: "MpSchoolOrderReportDetail",
          component: () => import("@/page/mp/school/order/report/detail/ReportDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "检验报告详情",
          build(reportId: string) {
            return `/mp/school/order/report/${reportId}`
          },
        },
      },
      Bid: {
        List: {
          path: "/mp/school/:schoolId/bid/project",
          name: "MpSchoolBidProjectList",
          component: () => import("@/page/mp/school/bid/ProjectListPage.vue").catch(catchImport),
          single: true,
          props: true,
          title: "竞标项目",
          build(schoolId: string) {
            return `/mp/school/${schoolId}/bid/project`
          },
        },
      },
      Inventory: {
        InboundRequest: {
          List: {
            path: "/mp/school/:schoolId/inventory/inbound/list",
            name: "MpSchoolInboundRequestList",
            component: () => import("@/page/mp/inventory/inbound/list/InboundRequestList.vue").catch(catchImport),
            single: true,
            props: true,
            title: "入库列表",
          },
          Detail: {
            path: "/mp/school/:schoolId/inventory/inbound/detail/:requestId",
            name: "MpSchoolInboundRequestDetail",
            component: () => import("@/page/mp/inventory/inbound/detail/InboundRequestDetail.vue").catch(catchImport),
            single: true,
            props: true,
            title: "入库详情",
          },
          Create: {
            path: "/mp/school/:schoolId/inventory/inbound/create",
            name: "MpSchoolInboundRequestCreate",
            component: () => import("@/page/mp/inventory/inbound/create/CreateInboundRequest.vue").catch(catchImport),
            single: true,
            props: true,
            title: "创建入库单",
          },
        },
        OutboundRequest: {
          List: {
            path: "/mp/school/:schoolId/inventory/outbound/list",
            name: "MpSchoolOutboundRequestList",
            component: () => import("@/page/mp/inventory/outbound/list/OutboundRequestList.vue").catch(catchImport),
            single: true,
            props: true,
            title: "出库列表",
          },
          Detail: {
            path: "/mp/school/:schoolId/inventory/outbound/detail/:requestId",
            name: "MpSchoolOutboundRequestDetail",
            component: () => import("@/page/mp/inventory/outbound/detail/OutboundRequestDetail.vue").catch(catchImport),
            single: true,
            props: true,
            title: "出库详情",
          },
          Create: {
            path: "/mp/school/:schoolId/inventory/outbound/create",
            name: "MpSchoolOutboundRequestCreate",
            component: () => import("@/page/mp/inventory/outbound/create/CreateOutboundRequest.vue").catch(catchImport),
            single: true,
            props: true,
            build(schoolId: string) {
              return `/mp/school/${schoolId}/inventory/outbound/create`
            },
            title: "创建出库单",
          },
          CreateByOrder: {
            path: "/mp/school/:schoolId/inventory/outbound/createByOrder/:orderId",
            name: "MpSchoolOutboundRequestCreateByOrder",
            component: () => import("@/page/mp/inventory/outbound/createByOrder/CreateOutboundRequestByOrder.vue").catch(catchImport),
            single: true,
            props: true,
            build(schoolId: string, orderId: string) {
              return `/mp/school/${schoolId}/inventory/outbound/createByOrder/${orderId}`
            },
          },
        },
        Stock: {
          List: {
            path: "/mp/school/:schoolId/inventory/stock/list",
            name: "MpSchoolStockList",
            component: () => import("@/page/mp/inventory/stock/list/StockList.vue").catch(catchImport),
            single: true,
            props: true,
            title: "库存列表",
          },
          Batch: {
            path: "/mp/school/:schoolId/inventory/stock/:stockId/batch",
            name: "MpSchoolStockBatch",
            component: () => import("@/page/mp/inventory/stock/batch/BatchList.vue").catch(catchImport),
            single: true,
            props: true,
            build(schoolId: string, stockId: string) {
              return `/mp/school/${schoolId}/inventory/stock/${stockId}/batch`
            },
            title: "批次详情",
          },
        },
        StockTake: {
          List: {
            path: "/mp/school/:schoolId/inventory/stockTake/list",
            name: "MpSchoolStockTakeList",
            component: () => import("@/page/mp/inventory/stockTake/list/StockTakeList.vue").catch(catchImport),
            single: true,
            props: true,
            title: "盘点列表",
          },
          Detail: {
            path: "/mp/school/:schoolId/inventory/stockTake/:stockTakeId/detail",
            name: "MpSchoolStockTakeDetail",
            component: () => import("@/page/mp/inventory/stockTake/detail/StockTakeDetail.vue").catch(catchImport),
            single: true,
            props: true,
            build(schoolId: string, stockTakeId: string) {
              return `/mp/school/${schoolId}/inventory/stockTake/${stockTakeId}/detail`
            },
            title: "盘点详情",
          },
        },
      },
      VendorManagement: {
        path: "/mp/school/:schoolId/vendorManagement",
        name: "MpSchoolVendorManagement",
        component: () => import("@/page/mp/school/vendorManagement/VendorManagementList.vue").catch(catchImport),
        single: true,
        props: true,
        title: "供应商管理",
        build(schoolId: string) {
          return `/mp/school/${schoolId}/vendorManagement`
        },
      },
      PreOrder: {
        List: {
          path: "/mp/school/:schoolId/bid/preOrder/list",
          name: "MpSchoolPreOrderList",
          component: () => import("@/page/mp/school/preOrder/list/PreOrderList.vue").catch(catchImport),
          single: true,
          props: true,
          build(schoolId: string) {
            return `/mp/school/${schoolId}/bid/preOrder/list`
          },
        },
      },
      Meal: {
        DailyDish: {
          Detail: {
            path: "/mp/school/:schoolId/meal/dailyDish/:dailyDishId/detail",
            name: "MpSchoolMealDailyDishDetail",
            component: () => import("@/page/mp/common/meal/dailyDish/detail/DailyDishDetail.vue").catch(catchImport),
            single: true,
            props: true,
            title: "每日菜品留样详情",
            build(schoolId: string, dailyDishId: string) {
              return `/mp/school/${schoolId}/meal/dailyDish/${dailyDishId}/detail`
            },
          },
        },
      },
      OpenKitchen: {
        DeviceManagement: {
          path: "/mp/school/openKitchen/deviceManagement",
          name: "MpSchoolOpenKitchenDeviceManagement",
          title: "监控视频",
          component: () => import("@/page/mp/school/openKitchen/deviceManagement/DeviceManagement.vue").catch(catchImport),
          single: true,
          props: true,
        },
        ViolationRecordList: {
          path: "/mp/school/openKitchen/violationRecordList",
          name: "MpSchoolOpenKitchenViolationRecordList",
          component: () => import("@/page/mp/school/openKitchen/violationRecordList/ViolationRecordList.vue").catch(catchImport),
          title: "违规记录",
          single: true,
          props: true,
        },
      },
      Ticket: {
        List: {
          path: "/mp/school/:schoolId/ticket/list",
          name: "MpSchoolTicketList",
          component: () => import("@/page/mp/school/ticket/list/TicketList.vue").catch(catchImport),
          single: true,
          props: true,
          title: "监管意见",
          build(schoolId: string) {
            return `/mp/school/${schoolId}/ticket/list`
          },
        },
        Detail: {
          path: "/mp/school/:schoolId/ticket/:ticketId/detail",
          name: "MpSchoolTicketDetail",
          component: () => import("@/page/mp/school/ticket/detail/TicketDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "监管意见详情",
          build(schoolId: string, ticketId: string) {
            return `/mp/school/${schoolId}/ticket/${ticketId}/detail`
          },
        },
      },
    },
    Qualification: {
      Management: {
        path: "/mp/qualification/management",
        name: "MpQualificationManagement",
        component: () => import("@/page/mp/qualification/management/QualificationManagement.vue").catch(catchImport),
        single: true,
        props: true,
        title: "资质管理",
        meta: {
          isKeepAlive: true,
        },
      },
      Detail: {
        path: "/mp/:type/group/:supervisionGroupId/qualification//detail/:id",
        name: "MPQualificationDetail",
        component: () => import("@/page/mp/qualification/detail/QualificationDetail.vue").catch(catchImport),
        single: true,
        props: true,
        build(type: "school" | "vendor", id: string, supervisionGroupId: string) {
          return `/mp/${type}/group/${supervisionGroupId}/qualification/detail/${id}`
        },
        title: "资质详情",
      },
    },
    OpenKitchen: {
      Management: {
        path: "/mp/openKitchen/management",
        name: "MpOpenkitchenManagement",
        title: "明厨亮灶",
        component: () => import("@/page/mp/openKitchen/management/OpenKitchenManagement.vue").catch(catchImport),
        single: true,
        props: true,
        meta: {
          isKeepAlive: true,
        },
      },
      DeviceManagement: {
        path: "/mp/openKitchen/deviceManagement",
        name: "MpOpenKitchenDeviceManagement",
        title: "监控视频",
        component: () => import("@/page/mp/openKitchen/deviceManagement/DeviceManagement.vue").catch(catchImport),
        single: true,
        props: true,
      },
      ViolationRecordList: {
        path: "/mp/openKitchen/violationRecordList",
        name: "MpOpenKitchenViolationRecordList",
        component: () => import("@/page/mp/openKitchen/violationRecordList/ViolationRecordList.vue").catch(catchImport),
        single: true,
        props: true,
      },
      LivePage: {
        // deprecated
        path: "/mp/openKitchen/live",
        name: "MpOpenKitchenLive",
        component: () => import("@/page/mp/openKitchen/live/Live.vue").catch(catchImport),
        single: true,
        props: true,
      },
    },
    LivePage: {
      path: "/live",
      name: "LivePage",
      component: () => import("@/page/mp/openKitchen/live/Live.vue").catch(catchImport),
      single: true,
      props: true,
    },
    Bid: {
      Announcement: {
        path: "/mp/bid/announcement/:projectId",
        name: "MpBidAnnouncement",
        component: () => import("@/page/mp/common/bid/AnnouncementPage.vue").catch(catchImport),
        single: true,
        props: true,
        title: "竞标结果公示",
        build(projectId: string) {
          return `/mp/bid/announcement/${projectId}`
        },
      },
    },
    Meal: {
      DailyDishDetail: {
        path: "/mp/meal/dailyDish/:dailyDishId/detail",
        name: "MpMealDailyDishDetail",
        component: () => import("@/page/mp/common/meal/dailyDish/detail/DailyDishDetail.vue").catch(catchImport),
        single: true,
        props: true,
        title: "每日菜品留样详情",
        build(dailyDishId: string) {
          return `/mp/meal/dailyDish/${dailyDishId}/detail`
        },
      },
    },
    Inspection: {
      Ticket: {
        List: {
          path: "/mp/inspection/ticket/list",
          name: "MpInspectionTicketList",
          component: () => import("@/page/mp/inspection/ticket/list/TicketList.vue").catch(catchImport),
          single: true,
          title: "监管意见",
        },
        Detail: {
          path: "/mp/inspection/ticket/:ticketId/detail",
          name: "MpInspectionTicketDetail",
          component: () => import("@/page/mp/inspection/ticket/detail/TicketDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "监管意见详情",
          build(ticketId: string, inspectionGroupId?: string) {
            let url = `/mp/inspection/ticket/${ticketId}/detail`
            if (inspectionGroupId) url += `?inspectionGroupId=${inspectionGroupId}`
            return url
          },
        },
      },
    },
    ResponsibleSupervisor: {
      Ticket: {
        List: {
          path: "/mp/responsibleSupervisor/ticket/list",
          name: "MpResponsibleSupervisorTicketList",
          component: () => import("@/page/mp/responsibleSupervisor/ticket/list/TicketList.vue").catch(catchImport),
          single: true,
          title: "监管意见",
        },
        Detail: {
          path: "/mp/responsibleSupervisor/ticket/:ticketId/detail",
          name: "MpResponsibleSupervisorTicketDetail",
          component: () => import("@/page/mp/responsibleSupervisor/ticket/detail/TicketDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "监管意见详情",
          build(ticketId: string) {
            return `/mp/responsibleSupervisor/ticket/${ticketId}/detail`
          },
        },
      },
    },
    Pdsc: {
      Ticket: {
        List: {
          path: "/mp/pdsc/ticket/list",
          name: "MpPdscTicketList",
          component: () => import("@/page/mp/pdsc/ticket/list/TicketList.vue").catch(catchImport),
          single: true,
          title: "监管意见",
          build(organizationId?: string) {
            let url = `/mp/pdsc/ticket/list`
            if (organizationId) url += `?organizationId=${organizationId}`
            return url
          },
        },
        Detail: {
          path: "/mp/pdsc/ticket/:ticketId/detail",
          name: "MpPdscTicketDetail",
          component: () => import("@/page/mp/pdsc/ticket/detail/TicketDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "监管意见详情",
          build(ticketId: string, organizationId?: string) {
            let url = `/mp/pdsc/ticket/${ticketId}/detail`
            if (organizationId) url += `?organizationId=${organizationId}`
            return url
          },
        },
      },
    },
    Vendor: {
      Order: {
        List: {
          path: "/mp/vendor/:vendorId/order",
          name: "MpVendorOrderList",
          component: () => import("@/page/mp/vendor/order/list/OrderList.vue").catch(catchImport),
          single: true,
          props: true,
          title: "我的订单",
          meta: { isKeepAlive: true },
        },
        Detail: {
          path: "/mp/vendor/:vendorId/order/:orderId/detail",
          name: "MpVendorOrderDetail",
          component: () => import("@/page/mp/vendor/order/detail/OrderDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "订单详情",
          meta: { isKeepAlive: true },
          build(vendorId: string, orderId: string) {
            return `/mp/vendor/${vendorId}/order/${orderId}/detail`
          },
        },
      },
      Bid: {
        List: {
          path: "/mp/vendor/:vendorId/bid/project",
          name: "MpVendorBidProjectList",
          component: () => import("@/page/mp/vendor/bid/ProjectListPage.vue").catch(catchImport),
          single: true,
          props: true,
          title: "竞标项目",
          build(vendorId: string) {
            return `/mp/vendor/${vendorId}/bid/project`
          },
        },
      },
      Credit: {
        List: {
          path: "/mp/vendor/:vendorId/credit/management",
          name: "MpVendorCreditList",
          component: () => import("@/page/mp/vendor/credit/list/CreditList.vue").catch(catchImport),
          single: true,
          props: true,
          title: "我的信用分",
          build(vendorId: string) {
            return `/mp/vendor/${vendorId}/credit/management`
          },
        },
      },
      OpenKitchen: {
        DeviceManagement: {
          path: "/mp/vendor/:vendorId/openKitchen/deviceManagement",
          name: "MpVendorOpenKitchenDeviceManagement",
          component: () => import("@/page/mp/vendor/openKitchen/deviceManagement/DeviceManagement.vue").catch(catchImport),
          single: true,
          props: true,
          title: "监控视频",
          build(vendorId: string) {
            return `/mp/vendor/${vendorId}/openKitchen/deviceManagement`
          },
        },
      },
      Ticket: {
        List: {
          path: "/mp/vendor/:vendorId/ticket/list",
          name: "MpVendorTicketList",
          component: () => import("@/page/mp/vendor/ticket/list/TicketList.vue").catch(catchImport),
          single: true,
          props: true,
          title: "监管意见",
          build(vendorId: string) {
            return `/mp/vendor/${vendorId}/ticket/list`
          },
        },
        Detail: {
          path: "/mp/vendor/:vendorId/ticket/:ticketId/detail",
          name: "MpVendorTicketDetail",
          component: () => import("@/page/mp/vendor/ticket/detail/TicketDetail.vue").catch(catchImport),
          single: true,
          props: true,
          title: "监管意见详情",
          build(vendorId: string, ticketId: string) {
            return `/mp/vendor/${vendorId}/ticket/${ticketId}/detail`
          },
        },
      },
    },
  },
  Common: {
    Order: {
      PrintOrder: {
        path: "/:role/order/:orderId/print",
        name: "CommonPrintOrder",
        component: () => import("@/page/common/order/print/PrintOrder.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        single: true,
        build(orderId: string, role: "school" | "vendor" | "regulator" | "manage") {
          return `/${role}/order/${orderId}/print`
        },
      },
    },
    License: {
      path: "/license",
      name: "License",
      component: () => import("@/settings/view/License.vue").catch(catchImport),
      single: true,
    },
    Settings: {
      path: "/settings",
      name: "Settings",
      component: () => import("@/settings/view/Settings.vue").catch(catchImport),
      props: true,
      requireAuth: true,
    },
    NotFound: {
      path: "/404",
      name: "404",
      component: () => import("@/common/view/404.vue").catch(catchImport),
      single: true,
    },
    Home: {
      path: "/",
      name: "Home",
      component: () => import("@/common/view/home/Home.vue").catch(catchImport),
      single: true,
      requireAuth: true,
    },
    Dashboard: {
      path: "/dashboard",
      name: "Dashboard",
      component: () => import("@/common/view/Dashboard.vue").catch(catchImport),
      requireAuth: true,
    },
  },
  User: {
    List: {
      path: "/user/list",
      name: "UserList",
      component: () => import("@/user/view/list/UserList.vue").catch(catchImport),
      props: true,
      requireAuth: true,
    },
    Detail: {
      path: "/user/:userId",
      name: "UserDetail",
      component: () => import("@/user/view/detail/UserDetail.vue").catch(catchImport),
      build(userId: string) {
        return `/user/${userId}`
      },
      props: true,
      requireAuth: true,
    },
  },
  Organization: {
    Basic: {
      List: {
        path: "/organization/list",
        name: "OrganizationList",
        component: () => import("@/organization/basic/view/list/OrganizationList.vue").catch(catchImport),
        requireAuth: true,
      },
      Detail: {
        path: "/organization/:organizationId",
        name: "OrganizationDetail",
        component: () => import("@/organization/basic/view/detail/OrganizationDetail.vue").catch(catchImport),
        build(organizationId: string) {
          return `/organization/${organizationId}`
        },
        requireAuth: true,
        props: true,
      },
    },
    Member: {
      List: {
        path: "/organization/:organizationId/member",
        name: "OrganizationMemberList",
        component: () => import("@/organization/basic/view/member/list/OrganizationMemberList.vue").catch(catchImport),
        build(organizationId: string) {
          return `/organization/${organizationId}/member`
        },
        props: true,
        requireAuth: true,
      },
    },
    Role: {
      List: {
        path: "/organization/:organizationId/role",
        name: "OrganizationRole",
        component: () => import("@/organization/role/view/list/RoleList.vue").catch(catchImport),
        build(organizationId: string) {
          return `/organization/${organizationId}/role`
        },
        props: true,
        requireAuth: true,
      },
      Detail: {
        path: "/organization/role/:roleId",
        name: "RoleDetail",
        component: () => import("@/organization/role/view/detail/RoleDetail.vue").catch(catchImport),
        build(roleId: string) {
          return `/organization/role/${roleId}`
        },
        props: true,
        requireAuth: true,
      },
    },
    Structure: {
      path: "/organization/:organizationId/structure",
      name: "OrganizationStructure",
      component: () => import("@/organization/structure/view/list/StructureList.vue").catch(catchImport),
      build(organizationId: string) {
        return `/organization/${organizationId}/structure`
      },
      props: true,
      requireAuth: true,
    },
  },
  Vendor: {
    Order: {
      List: {
        path: "/vendor/:vendorId/order/list",
        name: "VendorOrderManagement",
        component: () => import("@/page/vendor/order/list/OrderManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(vendorId: string) {
          return `/vendor/${vendorId}/order/list`
        },
      },
    },
    Credit: {
      path: "/vendor/:vendorId/credit",
      name: "VendorCredit",
      component: () => import("@/page/vendor/bid/credit/CreditManagement.vue").catch(catchImport),
      requireAuth: true,
      props: true,
      build(vendorId: string) {
        return `/vendor/${vendorId}/credit`
      },
    },
    Product: {
      Application: {
        List: {
          path: "/vendor/:vendorId/product/application/list",
          name: "VendorProductApplicationList",
          component: () => import("@/page/vendor/product/application/list/ProductApplicationList.vue").catch(catchImport),
          requireAuth: true,
          props: true,
          build(vendorId: string) {
            return `/vendor/${vendorId}/product/application/list`
          },
        },
        Create: {
          path: "/vendor/:vendorId/product/application/new",
          name: "VendorCreateProductApplication",
          component: () => import("@/page/vendor/product/application/detail/CreateProductApplication.vue").catch(catchImport),
          requireAuth: true,
          props: true,
          build(vendorId: string, setId: string) {
            return `/vendor/${vendorId}/product/application/new?setId=${setId}`
          },
        },
        Detail: {
          path: "/vendor/:vendorId/product/application/detail/:applicationId",
          name: "VendorProductApplicationDetail",
          component: () => import("@/page/vendor/product/application/detail/ProductApplicationDetail.vue").catch(catchImport),
          requireAuth: true,
          props: true,
          build(vendorId: string, applicationId: string) {
            const path = `/vendor/${vendorId}/product/application/detail/${applicationId}`
            return path
          },
        },
      },
      List: {
        path: "/vendor/:vendorId/product/list",
        name: "VendorProductManagement",
        component: () => import("@/page/vendor/product/list/ProductManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(vendorId: string) {
          return `/vendor/${vendorId}/product/list`
        },
      },
      Detail: {
        path: "/vendor/:vendorId/product/:productId/detail",
        name: "VendorProductDetail",
        component: () => import("@/page/vendor/product/detail/ProductDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(vendorId: string, productId: string) {
          return `/vendor/${vendorId}/product/${productId}/detail`
        },
      },
    },
    Quarantine: {
      Report: {
        List: {
          path: "/vendor/quarantine/report/list/:vendorId",
          name: "ReportList",
          component: () => import("@/page/vendor/quarantine/list/ReportList.vue").catch(catchImport),
          requireAuth: true,
          props: true,
          build(vendorId: string) {
            return `/vendor/quarantine/report/list/${vendorId}`
          },
        },
      },
    },
    UpperStream: {
      List: {
        path: "/vendor/upperStream/list/:vendorId",
        name: "UpperStreamList",
        component: () => import("@/page/vendor/upperStream/list/UpperStreamList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(vendorId: string) {
          return `/vendor/upperStream/list/${vendorId}`
        },
      },
    },
    Qualification: {
      path: "/vendor/qualification/:vendorId",
      name: "VendorInfo",
      component: () => import("@/page/vendor/qualification/info/VendorInfo.vue").catch(catchImport),
      requireAuth: true,
      props: true,
      build(vendorId: string) {
        return `/vendor/qualification/${vendorId}`
      },
    },
    Ticket: {
      List: {
        path: "/vendor/:vendorId/ticket/list",
        name: "VendorTicketList",
        component: () => import("@/page/vendor/ticket/list/TicketList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(vendorId: string) {
          return `/vendor/${vendorId}/ticket/list`
        },
      },
    },
    OpenKitchen: {
      Management: {
        path: "/vendor/:vendorId/openKitchen",
        name: "VendorOpenKitchenManagement",
        component: () => import("@/page/vendor/openKitchen/management/OpenKitchenManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(vendorId: string) {
          return `/vendor/${vendorId}/openKitchen`
        },
      },
    },
    BrandRegistry: {
      List: {
        path: "/vendor/:vendorId/brandRegistry/list",
        name: "VendorBrandRegistryList",
        component: () => import("@/page/vendor/bid/brandRegistry/BrandRegistryManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(vendorId: string) {
          return `/vendor/${vendorId}/brandRegistry/list`
        },
      },
    },
    Bid: {
      List: {
        path: "/vendor/:vendorId/bid/list",
        name: "VendorBidList",
        component: () => import("@/page/vendor/bid/management/Management.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(vendorId: string) {
          return `/vendor/${vendorId}/bid/list`
        },
      },
      ProjectDetail: {
        path: "/vendor/:vendorId/bid/project/:projectId",
        name: "VendorBidProjectDetail",
        component: () => import("@/page/vendor/bid/project/ProjectDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(vendorId: string, projectId: string) {
          return `/vendor/${vendorId}/bid/project/${projectId}`
        },
      },
      BidDetail: {
        path: "/vendor/:vendorId/bid/detail/:bidId",
        name: "VendorBidDetail",
        component: () => import("@/page/vendor/bid/bidding/detail/BidDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(vendorId: string, bidId: string) {
          return `/vendor/${vendorId}/bid/detail/${bidId}`
        },
      },
      BidEditor: {
        path: "/vendor/:vendorId/bid/editor/:bidId",
        name: "VendorBidEditor",
        component: () => import("@/page/vendor/bid/bidding/editor/BidDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(vendorId: string, bidId: string) {
          return `/vendor/${vendorId}/bid/editor/${bidId}`
        },
      },
    },
  },
  SchoolPublic: {
    Home: {
      path: "/school/public/home",
      name: "SchoolPublicHome",
      component: () => import("@/page/schoolPublic/home/Home.vue").catch(catchImport),
      requireAuth: false,
      single: true,
      build(schoolId: string, fixed?: "info" | "finance") {
        return `/school/public/home?schoolId=${schoolId}${fixed ? `&fixed=${fixed}` : ""}`
      },
    },
  },
  School: {
    Dashboard: {
      path: "/school/:schoolId/dashboard",
      name: "SchoolDashboard",
      component: () => import("@/page/school/dashboard/Dashboard.vue").catch(catchImport),
      requireAuth: true,
      props: true,
      build(schoolId: string) {
        return `/school/${schoolId}/dashboard`
      },
    },
    Meal: {
      Management: {
        path: "/school/meal/management/:schoolId",
        name: "BuyerMealManagement",
        component: () => import("@/page/school/meal/MealManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string) {
          return `/school/meal/management/${schoolId}`
        },
      },
    },
    Qualification: {
      path: "/school/qualification/:schoolId",
      name: "SchoolInfo",
      component: () => import("@/page/school/qualification/school/SchoolInfo.vue").catch(catchImport),
      requireAuth: true,
      props: true,
      build(schoolId: string, achor?: SchoolAnchor) {
        if (achor) {
          return `/school/qualification/${schoolId}#${achor}`
        } else {
          return `/school/qualification/${schoolId}`
        }
      },
    },
    Cafeteria: {
      List: {
        path: "/school/cafeteria/list/:schoolId",
        name: "SchoolCafeteriaList",
        component: () => import("@/page/school/cafeteria/CafeteriaList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string) {
          return `/school/cafeteria/list/${schoolId}`
        },
      },
    },
    Inventory: {
      Management: {
        path: "/school/inventory/management/:schoolId",
        name: "SchoolInventoryManagement",
        component: () => import("@/page/school/inventory/management/InventoryManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string) {
          return `/school/inventory/management/${schoolId}`
        },
      },
    },
    Order: {
      List: {
        path: "/buyer/order/list/:buyerId",
        name: "BuyerOrderList",
        component: () => import("@/page/school/order/view/list/OrderManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(buyerId: string) {
          return `/buyer/order/list/${buyerId}`
        },
      },
    },
    Product: {
      Application: {
        List: {
          path: "/buyer/:schoolId/product/application/list",
          name: "BuyerroductApplicationList",
          component: () => import("@/page/school/product/application/list/ProductApplicationList.vue").catch(catchImport),
          requireAuth: true,
          props: true,
          build(schoolId: string) {
            return `/buyer/${schoolId}/product/application/list`
          },
        },
      },
      List: {
        path: "/school/:schoolId/product/list",
        name: "SchoolProductList",
        component: () => import("@/page/school/product/list/ProductManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string) {
          return `/school/${schoolId}/product/list`
        },
      },
      Detail: {
        path: "/school/:schoolId/product/:productId/detail",
        name: "SchoolProductDetail",
        component: () => import("@/page/school/product/detail/ProductDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string, productId: string) {
          return `/school/${schoolId}/product/${productId}/detail`
        },
      },
    },
    Ticket: {
      List: {
        path: "/school/:schoolId/ticket/list",
        name: "SchoolTicketList",
        component: () => import("@/page/school/ticket/list/TicketList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string) {
          return `/school/${schoolId}/ticket/list`
        },
      },
    },
    Rectification: {
      List: {
        path: "/school/:schoolId/rectification/list",
        name: "SchoolRectificationList",
        component: () => import("@/page/school/rectification/list/RectificationList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string) {
          return `/school/${schoolId}/rectification/list`
        },
      },
    },
    Vendor: {
      Qualification: {
        path: "/school/:schoolId/group/:supervisionGroupId/vendor/:vendorId/qualification",
        name: "SchoolVendorInfo",
        component: () => import("@/page/school/vendor/qualification/VendorQualification.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string, supervisionGroupId: string, vendorId: string) {
          return `/school/${schoolId}/group/${supervisionGroupId}/vendor/${vendorId}/qualification`
        },
      },
    },
    OpenKitchen: {
      Management: {
        path: "/school/:schoolId/openKitchen",
        name: "SchoolOpenKitchenManagement",
        component: () => import("@/page/school/openKitchen/management/OpenKitchenManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string) {
          return `/school/${schoolId}/openKitchen`
        },
      },
    },
    Bid: {
      List: {
        path: "/school/:schoolId/bid/list",
        name: "SchoolBidList",
        component: () => import("@/page/school/bid/management/Management.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string) {
          return `/school/${schoolId}/bid/list`
        },
      },
      ProjectDetail: {
        path: "/school/:schoolId/bid/project/:projectId",
        name: "SchoolBidProjectDetail",
        component: () => import("@/page/school/bid/project/ProjectDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string, projectId: string) {
          return `/school/${schoolId}/bid/project/${projectId}`
        },
      },
      Credit: {
        path: "/school/:schoolId/credit",
        name: "SchoolCredit",
        component: () => import("@page/school/bid/credit/CreditManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string) {
          return `/school/${schoolId}/credit`
        },
      },
    },
  },
  Supervisor: {
    Qualification: {
      Management: {
        path: "/supervisor/:organizationId/qualification",
        name: "SupervisorQualificationManagement",
        component: () => import("@/page/supervisor/qualification/management/QualificationManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(organizationId: string) {
          return `/supervisor/${organizationId}/qualification`
        },
      },
      VendorQualification: {
        path: "/supervisor/group/:supervisionGroupId/qualification/vendor/:vendorId",
        name: "SupervisorVendorInfo",
        component: () => import("@/page/supervisor/qualification/vendorDetail/VendorQualification.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisionGroupId: string, vendorId: string, achor?: VendorAnchor) {
          if (achor) {
            return `/supervisor/group/${supervisionGroupId}/qualification/vendor/${vendorId}#${achor}`
          }
          return `/supervisor/group/${supervisionGroupId}/qualification/vendor/${vendorId}`
        },
      },
      SchoolQualification: {
        path: "/supervisor/qualification/school/:schoolId",
        name: "SupervisorSchoolInfo",
        component: () => import("@/page/supervisor/qualification/schoolDetail/SchoolQualification.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(schoolId: string, achor?: SchoolAnchor) {
          if (achor) {
            return `/supervisor/qualification/school/${schoolId}#${achor}`
          } else {
            return `/supervisor/qualification/school/${schoolId}`
          }
        },
      },
    },
    Ticket: {
      List: {
        path: "/supervisor/:organizationId/ticket/list",
        name: "SupervisorTicketList",
        component: () => import("@/page/educationBureau/ticket/list/TicketList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(organizationId: string) {
          return `/supervisor/${organizationId}/ticket/list`
        },
      },
    },
  },
  EducationBureau: {
    Dashboard: {
      path: "/educationBureau/:supervisorId/dashboard",
      name: "EducationBureauDashboard",
      component: () => import("@/page/educationBureau/dashboard/Dashboard.vue").catch(catchImport),
      requireAuth: true,
      props: true,
      build(supervisorId: string) {
        return `/educationBureau/${supervisorId}/dashboard`
      },
    },
    Meal: {
      SampleList: {
        path: "/educationBureau/:supervisorId/meal/sample",
        name: "EducationBureauMealSampleList",
        component: () => import("@/page/educationBureau/meal/sampleList/SampleList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/educationBureau/${supervisorId}/meal/sample`
        },
      },
    },
    Inventory: {
      Index: {
        path: "/educationBureau/:supervisorId/inventory",
        name: "EbInventoryIndex",
        component: () => import("@/page/educationBureau/inventory/InventoryIndex.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/educationBureau/${supervisorId}/inventory`
        },
      },
      Management: {
        path: "/educationBureau/:supervisorId/inventory/:schoolId/management",
        name: "EbInventoryManagement",
        component: () => import("@/page/educationBureau/inventory/management/InventoryManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string, schoolId: string) {
          return `/educationBureau/${supervisorId}/inventory/${schoolId}/management`
        },
      },
    },
    OpenKitchen: {
      Management: {
        path: "/educationBureau/:supervisorId/openKitchen",
        name: "EducationBureauOpenKitchenManagement",
        component: () => import("@/page/educationBureau/openKitchen/management/OpenKitchenManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/educationBureau/${supervisorId}/openKitchen`
        },
      },
      DeviceManagement: {
        path: "/educationBureau/:supervisorId/openKitchen/:organizationId/device",
        name: "EducationBureauOpenKitchenDeviceManagement",
        component: () => import("@/page/educationBureau/openKitchen/device/DeviceManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string, organizationId: string) {
          return `/educationBureau/${supervisorId}/openKitchen/${organizationId}/device`
        },
      },
    },
    Product: {
      Application: {
        List: {
          path: "/educationBureau/:supervisorId/product/application/list",
          name: "SupervisorroductApplicationList",
          component: () => import("@/page/educationBureau/product/application/ProductApplicationList.vue").catch(catchImport),
          requireAuth: true,
          props: true,
          build(supervisorId: string) {
            return `/educationBureau/${supervisorId}/product/application/list`
          },
        },
      },
      List: {
        path: "/educationBureau/:supervisorId/product/list",
        name: "SupervisorProductManagement",
        component: () => import("@/page/educationBureau/product/management/ProductManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/educationBureau/${supervisorId}/product/list`
        },
      },
      Detail: {
        path: "/educationBureau/:supervisorId/product/:productId/detail",
        name: "SupervisorProductDetail",
        component: () => import("@/page/educationBureau/product/detail/ProductDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string, productId: string) {
          return `/educationBureau/${supervisorId}/product/${productId}/detail`
        },
      },
      PriceManagement: {
        path: "/eb/:supervisorId/product/priceManagement",
        name: "EducationBureauProductPriceManagement",
        component: () => import("@/page/educationBureau/product/priceManagement/PriceManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/eb/${supervisorId}/product/priceManagement`
        },
      },
    },
    Credit: {
      path: "/educationBureau/:regulatorId/credit",
      name: "EducationBureauCredit",
      component: () => import("@page/educationBureau/bid/credit/CreditManagement.vue").catch(catchImport),
      requireAuth: true,
      props: true,
      build(regulatorId: string) {
        return `/educationBureau/${regulatorId}/credit`
      },
    },
    AllocationPlan: {
      Detail: {
        path: "/eb/:ebId/allocationPlan/detail/:allocationPlanId",
        name: "EducationBureauAllocationPlanDetail",
        component: () => import("@/page/educationBureau/bid/project/management/allocationPlan/AllocationPlanDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(ebId: string, allocationPlanId: string) {
          return `/eb/${ebId}/allocationPlan/detail/${allocationPlanId}`
        },
      },
    },
    Project: {
      Management: {
        path: "/eb/:ebId/project/list",
        name: "EducationBureauProjectManagement",
        component: () => import("@/page/educationBureau/bid/project/management/Management.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(ebId: string) {
          return `/eb/${ebId}/project/list`
        },
      },
      Editor: {
        path: "/eb/:ebId/project/editor/:projectId",
        name: "EducationBureauProjectEditor",
        component: () => import("@/page/educationBureau/bid/project/editor/ProjectEditor.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(ebId: string, projectId: string) {
          return `/eb/${ebId}/project/editor/${projectId}`
        },
      },
      Detail: {
        path: "/eb/:ebId/project/detail/:projectId",
        name: "EducationBureauProjectDetail",
        component: () => import("@/page/educationBureau/bid/project/detail/ProjectDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(ebId: string, projectId: string) {
          return `/eb/${ebId}/project/detail/${projectId}`
        },
      },
    },
    Order: {
      List: {
        path: "/educationBureau/:supervisorId/order/list",
        name: "SupervisorOrderManagement",
        component: () => import("@/page/educationBureau/order/OrderManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/educationBureau/${supervisorId}/order/list`
        },
      },
    },
    SupervisionCategory: {
      GroupList: {
        path: "/educationBureau/supervistion/categoryGroup/list/:supervisorId",
        name: "SupervisorCategoryGroupList",
        component: () => import("@/page/educationBureau/supervisionCategory/groupList/CategoryGroupList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/educationBureau/supervistion/categoryGroup/list/${supervisorId}`
        },
      },
      GroupDetail: {
        path: "/educationBureau/supervistion/categoryGroup/:supervisionGroupId",
        name: "SupervisorCategoryGroup",
        component: () => import("@/page/educationBureau/supervisionCategory/groupDetail/CategoryGroupDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisionGroupId: string) {
          return `/educationBureau/supervistion/categoryGroup/${supervisionGroupId}`
        },
      },
      NutritionGroupDetail: {
        path: "/educationBureau/supervistion/nutritionCategoryGroup/:supervisionGroupId",
        name: "SupervisorNutritionCategoryGroup",
        component: () => import("@/page/educationBureau/supervisionCategory/groupDetail/CategoryGroupDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        meta: {
          nutrition: true,
        },
        build(supervisionGroupId: string) {
          return `/educationBureau/supervistion/nutritionCategoryGroup/${supervisionGroupId}`
        },
      },
    },
    PDSCApplication: {
      List: {
        path: "/eb/:supervisorId/pdsc/application/list",
        name: "EBPDSCApplicationList",
        component: () => import("@/page/educationBureau/pdsc/PDSCApplicationList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/eb/${supervisorId}/pdsc/application/list`
        },
      },
      Detail: {
        path: "/eb/:supervisorId/pdsc/application/detail/:supervisionGroupId/:schoolId",
        name: "EBPDSCApplicationDetail",
        component: () => import("@/page/educationBureau/pdsc/PDSCApplicationDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string, supervisionGroupId: string, schoolId: string) {
          return `/eb/${supervisorId}/pdsc/application/detail/${supervisionGroupId}/${schoolId}`
        },
      },
    },
    Inspection: {
      List: {
        path: "/eb/:supervisorId/inspection/list",
        name: "InspectionList",
        component: () => import("@/page/educationBureau/inspection/groupList/InspectionGroupList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/eb/${supervisorId}/inspection/list`
        },
      },
    },
    BrandRegistry: {
      List: {
        path: "/eb/:ebId/brandRegistry/list",
        name: "EBBrandRegistryList",
        component: () => import("@/page/educationBureau/bid/brandRegistry/BrandRegistryManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(ebId: string) {
          return `/eb/${ebId}/brandRegistry/list`
        },
      },
    },
    ResponsibleSupervisor: {
      List: {
        path: "/eb/:supervisorId/responsibleSupervisor/list",
        name: "ResponsibleSupervisorList",
        component: () => import("@/page/educationBureau/responsibleSupervisor/ResponsibleSupervisorList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/eb/${supervisorId}/responsibleSupervisor/list`
        },
      },
    },
    Rectification: {
      List: {
        path: "/eb/:supervisorId/rectification/list",
        name: "EbRectificationList",
        component: () => import("@/page/educationBureau/rectification/list/RectificationList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/eb/${supervisorId}/rectification/list`
        },
      },
    },
    SchoolDistrict: {
      List: {
        path: "/eb/:supervisorId/schoolDistrict/list",
        name: "EbSchoolDistrictList",
        component: () => import("@/page/educationBureau/schoolDistrict/list/SchoolDistrictList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(supervisorId: string) {
          return `/eb/${supervisorId}/schoolDistrict/list`
        },
      },
    },
  },
  Inspection: {
    Center: {
      path: "/inspection/center",
      name: "InspectionCenter",
      component: () => import("@/page/inspection/center/InspectionCenter.vue").catch(catchImport),
      requireAuth: true,
      props: true,
      build() {
        return `/inspection/center`
      },
    },
    Ticket: {
      List: {
        path: "/inspection/ticket/list",
        name: "InspectionTicketList",
        component: () => import("@/page/inspection/ticket/list/TicketList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build() {
          return `/inspection/ticket/list`
        },
      },
    },
  },
  ResponsibleSupervisor: {
    Center: {
      path: "/responsibleSupervisor/center",
      name: "ResponsibleSupervisorCenter",
      component: () => import("@/page/responsibleSupervisor/center/ResponsibleSupervisorCenter.vue").catch(catchImport),
      requireAuth: true,
      props: true,
      build() {
        return `/responsibleSupervisor/center`
      },
    },
    Ticket: {
      List: {
        path: "/responsibleSupervisor/ticket/list",
        name: "ResponsibleSupervisorTicketList",
        component: () => import("@/page/responsibleSupervisor/ticket/list/TicketList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build() {
          return `/responsibleSupervisor/ticket/list`
        },
      },
    },
  },
  Pdsc: {
    Ticket: {
      List: {
        path: "/pdsc/:organizationId/ticket/list",
        name: "PdscTicketList",
        component: () => import("@/page/pdsc/ticket/list/TicketList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(organizationId: string) {
          return `/pdsc/${organizationId}/ticket/list`
        },
      },
    },
  },
  SAMR: {},
  Supervision: {
    Group: {
      List: {
        path: "/supervision/list",
        name: "SupervisionGroupList",
        component: () => import("@/supervision/view/group/list/SupervisionGroupList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
      },
      Detail: {
        path: "/supervision/detail/:groupId",
        name: "SupervisionGroupDetail",
        component: () => import("@/supervision/view/group/detail/SupervisionGroupDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(groupId: string) {
          return `/supervision/detail/${groupId}`
        },
      },
      SubGroupShortlist: {
        path: "/supervision/detail/:groupId/subGroup/:subGroupId/shortlist",
        name: "SupervisionSubGroupShortlist",
        component: () => import("@/supervision/view/group/detail/subGroupShortlist/SubGroupShortlistList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(groupId: string, subGroupId: string, subGroupName: string) {
          return `/supervision/detail/${groupId}/subGroup/${subGroupId}/shortlist?subGroupName=${encodeURIComponent(subGroupName)}`
        },
      },
      SubGroupLotType: {
        path: "/supervision/detail/:groupId/subGroup/:subGroupId/lotType",
        name: "SupervisionSubGroupLotType",
        component: () => import("@/supervision/view/group/detail/subGroupLotType/SubGroupLotTypeList.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(groupId: string, subGroupId: string, subGroupName: string) {
          return `/supervision/detail/${groupId}/subGroup/${subGroupId}/lotType?subGroupName=${encodeURIComponent(subGroupName)}`
        },
      },
    },
  },
  Manage: {
    Contract: {
      List: {
        path: "/manage/contract/list",
        name: "ManageContractList",
        component: () => import("@/page/manage/contract/list/ContractList.vue").catch(catchImport),
        requireAuth: true,
      },
    },
    OrderManagement: {
      path: "/manage/order",
      name: "ManageOrderManagement",
      component: () => import("@/page/manage/order/OrderManagement.vue").catch(catchImport),
      requireAuth: true,
    },
    PreOrderManagement: {
      path: "/manage/preOrder",
      name: "ManagePreOrderManagement",
      component: () => import("@/page/manage/preOrder/PreOrderManagement.vue").catch(catchImport),
      requireAuth: true,
    },
    FoodMaterialList: {
      path: "/manage/foodMaterial/list",
      name: "ManageFoodManageList",
      component: () => import("@/page/manage/foodMaterial/FoodMaterialList.vue").catch(catchImport),
      requireAuth: true,
    },
    OpenKitchen: {
      DeviceManagement: {
        path: "/manage/openKitchen/deviceManagement",
        name: "ManageOpenKitchenDeviceManagement",
        component: () => import("@/page/manage/openKitchen/device/DeviceManagement.vue").catch(catchImport),
        requireAuth: true,
      },
      ViotlationRecord: {
        path: "/manage/openKitchen/violationRecord/list",
        name: "ManageViotlationRecordList",
        component: () => import("@/page/manage/openKitchen/violationRecord/list/ViolationRecordList.vue").catch(catchImport),
        requireAuth: true,
      },
    },
    SupervisionRecord: {
      path: "/manage/supervision/record/list",
      name: "ManageSupervisionRecordList",
      component: () => import("@/page/manage/supervision/list/SupervisionRecordList.vue").catch(catchImport),
      requireAuth: true,
    },
    Storage: {
      path: "/manage/storage",
      name: "StorageManagement",
      component: () => import("@/manage/storage/view/StorageManagement.vue").catch(catchImport),
      requireAuth: true,
    },
    CacheManagement: {
      path: "/manage/cache",
      name: "CacheManagement",
      component: () => import("@/manage/view/CacheManagement.vue").catch(catchImport),
      requireAuth: true,
    },
    SystemMigration: {
      path: "/manage/migration",
      name: "SystemMigration",
      component: () => import("@/manage/migration/view/Migration.vue").catch(catchImport),
      requireAuth: true,
    },
    YmtCredential: {
      List: {
        path: "/manage/ymt/credential/list",
        name: "ManageYmtCredentialList",
        component: () => import("@/page/manage/ymt/list/YmtCredentialList.vue").catch(catchImport),
        requireAuth: true,
      },
    },
    Product: {
      Application: {
        List: {
          path: "/manage/product/:groupId/application/list",
          name: "ManageProductApplicationList",
          component: () => import("@/page/manage/product/application/ProductApplicationList.vue").catch(catchImport),
          requireAuth: true,
          props: true,
          build(groupId: string) {
            return `/manage/product/${groupId}/application/list`
          },
        },
      },
      List: {
        path: "/manage/product/:groupId/list",
        name: "ManageProductManagement",
        component: () => import("@/page/manage/product/management/ProductManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(groupId: string) {
          return `/manage/product/${groupId}/list`
        },
      },
      Detail: {
        path: "/manage/product/:groupId/:productId/detail",
        name: "ManageProductDetail",
        component: () => import("@/page/manage/product/detail/ProductDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(groupId: string, productId: string) {
          return `/manage/product/${groupId}/${productId}/detail`
        },
      },
      PriceManagement: {
        path: "/manage/product/:groupId/priceManagement",
        name: "ManageProductPriceManagement",
        component: () => import("@/page/manage/product/priceManagement/PriceManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(groupId: string) {
          return `/manage/product/${groupId}/priceManagement`
        },
      },
    },
    Hierarchy: {
      List: {
        path: "/manage/hierarchy/list",
        name: "ManageHierarchyList",
        component: () => import("@/page/manage/hierarchy/list/HierarchyList.vue").catch(catchImport),
        requireAuth: true,
      },
    },
    Bid: {
      ProjectList: {
        path: "/manage/bid/project",
        name: "ManageBidProjectList",
        component: () => import("@/page/manage/bid/projectList/ProjectList.vue").catch(catchImport),
        requireAuth: true,
      },
      ProjectDetail: {
        path: "/manage/bid/project/:projectId",
        name: "ManageBidProjectDetail",
        component: () => import("@/page/manage/bid/projectDetail/ProjectDetail.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(projectId: string) {
          return `/manage/bid/project/${projectId}`
        },
      },
    },
  },
  OpenKitchen: {
    Operator: {
      DeviceManagement: {
        path: "/openKitchen/operator/:organizationId/deviceManagement",
        name: "OperatorOpenKitchenDeviceManagement",
        component: () => import("@/page/operator/openKitchen/device/DeviceManagement.vue").catch(catchImport),
        requireAuth: true,
        props: true,
        build(organizationId: string) {
          return `/openKitchen/operator/${organizationId}/deviceManagement`
        },
      },
    },
  },
}

export default Routes
